import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, resolveComponent as _resolveComponent, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "mb-l pb-xs border-gray-200 border-b-2 flex items-center justify-between" }
const _hoisted_2 = { class: "mt-4" }

import RoundedCard from "@/components/RoundedCard.vue";
import {
  encryptCareProviderGroupKeyForCareProviderGroup,
  bytesToHex,
  generateRandomSalt,
  passwordToKeyPair,
} from "@/helpers/encryption";
import { Profile } from "@/interfaces/profile";
import { ref, onMounted } from "vue";

import { useSessionStore } from "@/store/sessionStore";
import { useAccountStore } from "@/store/accountStore";
import UmanaHeadline from "@/components/UmanaHeadline.vue";
import { supabase } from "@/helpers/supabase";
import ListTile from "@/components/ListTile.vue";
import CoreIcon from "@/components/CoreIcon.vue";
import DialogCard from "@/components/DialogCard.vue";
import { useNotificationStore } from "@/store/notificationStore";
import _sodium from "libsodium-wrappers-sumo";


export default /*#__PURE__*/_defineComponent({
  __name: 'ProfileCard',
  setup(__props) {

const accountStore = useAccountStore();
const sessionStore = useSessionStore();
const profileModel = ref<{ firstName?: string; lastName?: string }>({});
const passwordModel = ref<any>("");
const showPasswordSettings = ref<boolean>(false);
const emailModel = ref<any>("");
const showProfileSettings = ref<boolean>(false);
const showEmailSettings = ref<boolean>(false);

onMounted(() => {
  profileModel.value = {
    firstName: accountStore.firstName ?? "",
    lastName: accountStore.lastName ?? "",
  };
});

// password
async function updatePassword() {
  await wait();
  const currentKeys = useAccountStore().careProviderGroupPrivateKeys;

  if (currentKeys == null || passwordModel.value.length == 0) return;

  const salt = await generateRandomSalt();
  const saltAsHex = bytesToHex(salt);

  const keyPair = await passwordToKeyPair(passwordModel.value, saltAsHex);
  await _sodium.ready;
  const sodium = _sodium;
  const newKey = {
    public_key: bytesToHex(keyPair.publicKey),
    opslimit: sodium.crypto_pwhash_OPSLIMIT_MODERATE,
    algorithm: sodium.crypto_pwhash_ALG_DEFAULT,
    memlimit: sodium.crypto_pwhash_MEMLIMIT_MODERATE,
    salt: saltAsHex,
  };

  let newKeys = Array<string>();
  let newKeyIds = Array<number>();

  for (let key of currentKeys) {
    newKeys.push(
      bytesToHex(
        await encryptCareProviderGroupKeyForCareProviderGroup(
          keyPair.publicKey,
          key[1].key
        )
      )
    );
    newKeyIds.push(key[0]);
  }

  sessionStore.keyPair = keyPair;

  let res = await supabase.rpc("update_my_care_provider_group_keys", {
    _private_key_ids: newKeyIds,
    _encrypted_private_keys: newKeys,
    _new_public_key: newKey.public_key,
    _new_salt: saltAsHex,
    _new_opslimit: newKey.opslimit,
    _new_memlimit: newKey.memlimit,
    _new_algorithm: newKey.algorithm,
  });
  if (res.error == null) {
    useNotificationStore().notifications.push({
      msg: `Ihr Passwort wurde geändert. Das alte Passwort wurde gelöscht.`,
      type: "success",
    });

    showPasswordSettings.value = false;
  } else {
    useNotificationStore().notifications.push({
      msg: `Ihr Passwort wurde nicht geändert, weil ein Fehler aufgetreten ist. Bitte laden Sie die Seite neu und versuchen Sie es später erneut. #p01: ${res.error.message}`,
      type: "danger",
    });
    showPasswordSettings.value = false;
  }
}

async function wait() {
  await new Promise((resolve) => setTimeout(resolve, 100));
}

// Profile

async function saveProfile() {
  let profile = await supabase
    .from("profile")
    .upsert([
      {
        first_name: profileModel.value.firstName,
        last_name: profileModel.value.lastName,
      },
    ])
    .select()

    .returns<Profile>()
    .single();
  accountStore.setProfile(profile.data);
  showProfileSettings.value = false;
}

/// Email
async function saveEmail() {
  await supabase.auth.updateUser(
    {
      email: emailModel.value,
    },
    {
      emailRedirectTo: "https://cockpit.umana.ch",
    }
  );
  useNotificationStore().notifications.push({
    msg: `Bitte bestätigen Sie die neue E-Mail-Adresse, indem Sie das Bestätigungsmail anklicken.`,
    type: "info",
  });
  showEmailSettings.value = false;
}

return (_ctx: any,_cache: any) => {
  const _component_FormKit = _resolveComponent("FormKit")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(RoundedCard, null, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          _createVNode(UmanaHeadline, { h: "h2" }, {
            default: _withCtx(() => [
              _createTextVNode("Ihr Profil")
            ]),
            _: 1
          })
        ]),
        _createElementVNode("ul", _hoisted_2, [
          _createVNode(ListTile, null, {
            main: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(accountStore).firstName) + " " + _toDisplayString(_unref(accountStore).lastName), 1)
            ]),
            trailing: _withCtx(() => [
              _createVNode(CoreIcon, {
                icon: "pen",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (showProfileSettings.value = true))
              })
            ]),
            _: 1
          }),
          _createVNode(ListTile, null, {
            main: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(sessionStore).session?.user.email ?? ""), 1)
            ]),
            trailing: _withCtx(() => [
              _createVNode(CoreIcon, {
                icon: "pen",
                onClick: _cache[1] || (_cache[1] = ($event: any) => (showEmailSettings.value = true))
              })
            ]),
            _: 1
          }),
          _createVNode(ListTile, null, {
            main: _withCtx(() => [
              _createTextVNode(" Passwort ändern ")
            ]),
            trailing: _withCtx(() => [
              _createVNode(CoreIcon, {
                icon: "pen",
                onClick: _cache[2] || (_cache[2] = ($event: any) => (showPasswordSettings.value = true))
              })
            ]),
            _: 1
          })
        ])
      ]),
      _: 1
    }),
    _createVNode(DialogCard, {
      color: "gray",
      "is-visible": showProfileSettings.value,
      "show-footer": false,
      title: "Profileinstellungen",
      onClose: _cache[4] || (_cache[4] = ($event: any) => (showProfileSettings.value = false))
    }, {
      default: _withCtx(() => [
        _createVNode(_component_FormKit, {
          type: "form",
          onSubmit: saveProfile,
          modelValue: profileModel.value,
          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((profileModel).value = $event)),
          "submit-label": "Profil aktualisieren"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_FormKit, {
              type: "text",
              placeholder: "Muster",
              label: "Nachname",
              name: "lastName",
              validation: "required"
            }),
            _createVNode(_component_FormKit, {
              type: "text",
              placeholder: "Max",
              label: "Vorname",
              name: "firstName",
              validation: "required"
            })
          ]),
          _: 1
        }, 8, ["modelValue"])
      ]),
      _: 1
    }, 8, ["is-visible"]),
    _createVNode(DialogCard, {
      color: "gray",
      "is-visible": showEmailSettings.value,
      "show-footer": false,
      title: "E-Mail",
      onClose: _cache[6] || (_cache[6] = ($event: any) => (showEmailSettings.value = false))
    }, {
      default: _withCtx(() => [
        _createVNode(_component_FormKit, {
          type: "form",
          onSubmit: saveEmail,
          "submit-label": "E-Mail aktualisieren"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_FormKit, {
              type: "email",
              placeholder: _unref(sessionStore).session?.user.email ?? '',
              label: "E-Mail",
              validation: "required",
              modelValue: emailModel.value,
              "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((emailModel).value = $event))
            }, null, 8, ["placeholder", "modelValue"])
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["is-visible"]),
    _createVNode(DialogCard, {
      color: "gray",
      "is-visible": showPasswordSettings.value,
      "show-footer": false,
      title: "Passwort",
      onClose: _cache[8] || (_cache[8] = ($event: any) => (showPasswordSettings.value = false))
    }, {
      default: _withCtx(() => [
        _createVNode(_component_FormKit, {
          type: "form",
          onSubmit: updatePassword,
          "submit-label": "Passwort aktualisieren"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_FormKit, {
              type: "password",
              placeholder: "Neues Passwort",
              label: "Passwort",
              name: "password",
              validation: "required",
              modelValue: passwordModel.value,
              "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((passwordModel).value = $event))
            }, null, 8, ["modelValue"]),
            _createVNode(_component_FormKit, {
              type: "password",
              label: "Passwort bestätigen",
              name: "password_confirm",
              validation: "required|confirm",
              placeholder: "Neues Passwort"
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["is-visible"])
  ], 64))
}
}

})